import { hasCapability, type User } from "../base";

const quoteAdminTools = {
  update: (user: User) => hasCapability(user, "adminTools.quoteAdminTools.update"),
  read: (user: User) => hasCapability(user, "adminTools.quoteAdminTools.read"),
};

const quoteEngineerTools = {
  update: (user: User) => hasCapability(user, "adminTools.quoteEngineerTools.update"),
};

const propertyModel = {
  read: (user: User) => hasCapability(user, "adminTools.propertyModel.read"),
};

const salesAdminTools = {
  read: (user: User) => hasCapability(user, "adminTools.salesAdminTools.read"),
};

const opsAdminTools = {
  read: (user: User) => hasCapability(user, "adminTools.opsAdminTools.read"),
};

const materialsAdminTools = {
  read: (user: User) => hasCapability(user, "adminTools.materialsAdminTools.read"),
  update: (user: User) => hasCapability(user, "adminTools.materialsAdminTools.update"),
};

const invoiceAdminTools = {
  read: (user: User) => hasCapability(user, "adminTools.invoiceAdminTools.read"),
  update: (user: User) => hasCapability(user, "adminTools.invoiceAdminTools.update"),
  attachEscrow: (user: User) => hasCapability(user, "adminTools.invoiceAdminTools.attachEscrow"),
};

const blockersAdminTools = {
  read: (user: User) => hasCapability(user, "adminTools.blockersAdminTools.read"),
};

const mobileAdminTools = {
  read: (user: User) => hasCapability(user, "adminTools.mobileAdminTools.read"),
  // Capabilities resolve from the impersonated person, so an admin impersonating a contractor loses the
  // mobile tools mid-session — which is exactly when the seeding ones are wanted. The acting admin
  // lives in `imposter`.
  readAsActingAdmin: (user: User & { imposter?: User["person"] }) =>
    mobileAdminTools.read({ ...user, person: user.imposter ?? user.person }),
};

const geographicAreaAdminTools = {
  read: (user: User) => hasCapability(user, "adminTools.geographicAreaAdminTools.read"),
};

const anomaliesAdminTools = {
  read: (user: User) => hasCapability(user, "adminTools.anomaliesAdminTools.read"),
  resolve: (user: User) => hasCapability(user, "adminTools.anomaliesAdminTools.resolve"),
};

// Capabilities resolve from the impersonated person, so without the imposter denial
// anyone with impersonation.create could read this page as a holder.
const impersonationAuditTools = {
  read: (user: User & { imposter?: unknown; imposterId?: string | null }) =>
    !user.imposter && !user.imposterId && hasCapability(user, "adminTools.impersonationAuditTools.read"),
};

const proxyPhoneAdminTools = {
  read: (user: User) => hasCapability(user, "adminTools.proxyPhoneAdminTools.read"),
  update: (user: User) => hasCapability(user, "adminTools.proxyPhoneAdminTools.update"),
};

const telecomMappingAdminTools = {
  read: (user: User) => hasCapability(user, "adminTools.telecomMappingAdminTools.read"),
  update: (user: User) => hasCapability(user, "adminTools.telecomMappingAdminTools.update"),
};

const estimateMappings = {
  read: (user: User) => hasCapability(user, "adminTools.estimateMappings.read"),
};

const contractorCapabilityGroupings = {
  read: (user: User) => hasCapability(user, "adminTools.contractorCapabilityGroupings.read"),
  create: (user: User) => hasCapability(user, "adminTools.contractorCapabilityGroupings.create"),
  update: (user: User) => hasCapability(user, "adminTools.contractorCapabilityGroupings.update"),
  delete: (user: User) => hasCapability(user, "adminTools.contractorCapabilityGroupings.delete"),
};

const estimateQaChecks = {
  read: (user: User) => hasCapability(user, "adminTools.estimateQaChecks.read"),
  create: (user: User) => hasCapability(user, "adminTools.estimateQaChecks.create"),
  update: (user: User) => hasCapability(user, "adminTools.estimateQaChecks.update"),
  delete: (user: User) => hasCapability(user, "adminTools.estimateQaChecks.delete"),
};

// Borrows the ops admin capability: any new Capability value currently breaks the SMS template
// types (ODD-11544). Same holders as dedicated SLA capabilities would have.
const slaDefinitions = {
  read: (user: User) => hasCapability(user, "adminTools.opsAdminTools.read"),
  rebuild: (user: User) => hasCapability(user, "adminTools.opsAdminTools.read"),
};

const portalAdminTools = {
  resetFinancing: (user: User) => hasCapability(user, "adminTools.portalAdminTools.resetFinancing"),
};

const changeOrderAdminTools = {
  seedLegacyLineItem: (user: User) => hasCapability(user, "adminTools.changeOrderAdminTools.seedLegacyLineItem"),
};

const apiKeyAdminTools = {
  generate: (user: User) => hasCapability(user, "adminTools.apiKeyAdminTools.generate"),
  read: (user: User) => hasCapability(user, "adminTools.apiKeyAdminTools.read"),
  manage: (user: User) => hasCapability(user, "adminTools.apiKeyAdminTools.manage"),
};

const unknownContactReconciliation = {
  read: (user: User) => hasCapability(user, "adminTools.unknownContactReconciliation.read"),
  update: (user: User) => hasCapability(user, "adminTools.unknownContactReconciliation.update"),
};

const userAdminTools = {
  manage: (user: User) => hasCapability(user, "adminTools.userAdminTools.manage"),
};

const accountManagementMapAdminTools = {
  read: (user: User) => hasCapability(user, "adminTools.accountManagementMapAdminTools.read"),
  update: (user: User) => hasCapability(user, "adminTools.accountManagementMapAdminTools.update"),
};

const woExpiryAdminTools = {
  read: (user: User) => hasCapability(user, "adminTools.woExpiryAdminTools.read"),
  update: (user: User) => hasCapability(user, "adminTools.woExpiryAdminTools.update"),
};

const trainingAdminTools = {
  manage: (user: User) => hasCapability(user, "adminTools.trainingAdminTools.manage"),
};

export const adminTools = {
  read: (user: User) => hasCapability(user, "adminTools.read"),
  quoteAdminTools,
  quoteEngineerTools,
  propertyModel,
  salesAdminTools,
  invoiceAdminTools,
  opsAdminTools,
  blockersAdminTools,
  mobileAdminTools,
  materialsAdminTools,
  anomaliesAdminTools,
  impersonationAuditTools,
  proxyPhoneAdminTools,
  telecomMappingAdminTools,
  geographicAreaAdminTools,
  contractorCapabilityGroupings,
  estimateMappings,
  estimateQaChecks,
  slaDefinitions,
  portalAdminTools,
  apiKeyAdminTools,
  changeOrderAdminTools,
  unknownContactReconciliation,
  userAdminTools,
  accountManagementMapAdminTools,
  woExpiryAdminTools,
  trainingAdminTools,
};
